import { Card } from '@/models/card';
import { PreOrder } from '@/models/order';
import { SKUSpecification } from '@/models/sku';
import { User, UserFullInfo } from '@/models/user';
import { STORAGE_POS_PREORDER } from '@/utils/storage';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * POS 收银页购物车数据状态管理 Store (Zustand)
 *
 * 仅管理待结清单 preOrder（商品、绑定客户、待购套餐卡）的数据与操作；
 * 结算弹窗的开关与模式（mode）归属 useSettlementStore（结算流程域），
 * 跨 store 依赖方向：流程域 → 数据域。
 * 使用 immer middleware：preOrder 存在 userFullInfo.user 两层嵌套与 specifications 数组
 * 增删改，mutate 直写与项目组件层 useImmer 惯例一致。
 * 纯状态 Store：业务校验（如库存）在 action 内完成并以返回值告知调用方，UI 反馈
 * （message / playSound）由组件层处理。
 */
export interface PreOrderStoreState {
  // === 状态 ===
  /** 待结清单（购物车 + 绑定客户 + 套餐卡），始终为对象 */
  preOrder: PreOrder;

  // === Actions ===
  /** 整体重置（POS 页面卸载清理用）：空购物车 */
  reset: () => void;
  /** 仅清空 preOrder：开始服务 / 推入服务台 / CART 结算完成 */
  resetPreOrder: () => void;
  /** 清空购物车但保留已绑定用户：撤单 */
  resetKeepingUserFullInfo: () => void;
  /** 整体替换 preOrder：服务台整单载入 */
  setPreOrder: (preOrder: PreOrder) => void;
  /** 绑定用户（扫码/搜索绑定） */
  bindUser: (userFullInfo: UserFullInfo) => void;
  /** 已绑定用户时才更新用户信息（如发放优惠券后） */
  updateUserFullInfo: (userFullInfo: UserFullInfo) => void;
  /** 解绑用户 */
  unbindUser: () => void;
  /** 加购商品（含库存校验）；库存不足返回 false 且不加 */
  addSpecification: (specification: SKUSpecification) => boolean;
  /** 购卡：一次只能购买一张卡（进入购卡模式由结算域 open 处理） */
  addCard: (card: Card) => void;
  /** 清空待购套餐卡：购卡模式取消购买 */
  clearCards: () => void;
  /** 修改商品数量：clamp >= 1 + 库存校验；库存不足返回 false */
  changeSpecificationNumber: (id: number, num: number) => boolean;
  /** 移除购物车中的商品 */
  deleteSpecification: (id: number) => void;
  /** 清空购物车商品 */
  clearSpecifications: () => void;
  /** 充值支付成功：更新用户余额信息 */
  rechargeReceived: (user: User) => void;
}

const createInitialState = () =>
  ({
    preOrder: {
      specifications: [],
      userFullInfo: null,
      cards: [],
      startTime: undefined,
    },
  }) as PreOrderStoreState;

const actionName = (name: string) => `preOrder/${name}`;

/** 目标总数是否超过库存 */
const canSetNumber = (spec: SKUSpecification, number: number): boolean => {
  return number <= spec.stock;
};

export const usePreOrderStore = create<PreOrderStoreState>()(
  devtools(
    immer(
      persist(
        (set, get) => ({
          ...createInitialState(),

          reset: () => {
            set({ preOrder: createInitialState().preOrder }, false, actionName('reset'));
          },

          resetPreOrder: () => {
            set(
              state => {
                state.preOrder = createInitialState().preOrder;
              },
              false,
              actionName('resetPreOrder')
            );
          },

          resetKeepingUserFullInfo: () => {
            set(
              state => {
                const userFullInfo = state.preOrder.userFullInfo;
                state.preOrder = {
                  ...createInitialState().preOrder,
                  userFullInfo,
                };
              },
              false,
              actionName('resetKeepingUserFullInfo')
            );
          },

          setPreOrder: preOrder => {
            set({ preOrder }, false, actionName('setPreOrder'));
          },

          bindUser: userFullInfo => {
            set(
              state => {
                state.preOrder.userFullInfo = userFullInfo;
              },
              false,
              actionName('bindUser')
            );
          },

          updateUserFullInfo: userFullInfo => {
            set(
              state => {
                // 如果当前已经关联了客户，则更新客户信息
                if (state.preOrder.userFullInfo) {
                  state.preOrder.userFullInfo = userFullInfo;
                }
              },
              false,
              actionName('updateUserFullInfo')
            );
          },

          unbindUser: () => {
            set(
              state => {
                state.preOrder.userFullInfo = null;
              },
              false,
              actionName('unbindUser')
            );
          },

          addSpecification: specification => {
            const { preOrder } = get();
            const specifications = preOrder.specifications || [];
            const findIndex = specifications.findIndex(exists => exists.id === specification.id);
            const currentNumber = findIndex > -1 ? specifications[findIndex].number : 0;

            // 库存校验：购物车内该商品数量 + 本次新增数量不超过库存
            if (!canSetNumber(specification, currentNumber + specification.number)) {
              return false;
            }

            set(
              state => {
                if (!state.preOrder.specifications) {
                  state.preOrder.specifications = [];
                }
                if (findIndex === -1) {
                  state.preOrder.specifications.unshift(specification);
                } else {
                  state.preOrder.specifications[findIndex].number += specification.number;
                }
              },
              false,
              actionName('addSpecification')
            );
            return true;
          },

          addCard: card => {
            set(
              state => {
                // 一次只能购买一张卡
                state.preOrder.cards = [card];
              },
              false,
              actionName('addCard')
            );
          },

          clearCards: () => {
            set(
              state => {
                state.preOrder.cards = [];
              },
              false,
              actionName('clearCards')
            );
          },

          changeSpecificationNumber: (id, num) => {
            num = Math.max(num, 1);
            const { preOrder } = get();
            const specifications = preOrder.specifications || [];
            const findIndex = specifications.findIndex(i => i.id === id);
            if (findIndex === -1) {
              return false;
            }

            // 库存校验：以 store 内当前商品为权威源
            if (!canSetNumber(specifications[findIndex], num)) {
              return false;
            }

            set(
              state => {
                state.preOrder.specifications[findIndex].number = num;
              },
              false,
              actionName('changeSpecificationNumber')
            );
            return true;
          },

          deleteSpecification: id => {
            set(
              state => {
                const specifications = state.preOrder.specifications || [];
                const findIndex = specifications.findIndex(i => i.id === id);
                if (findIndex > -1) {
                  specifications.splice(findIndex, 1);
                }
              },
              false,
              actionName('deleteSpecification')
            );
          },

          clearSpecifications: () => {
            set(
              state => {
                if (state.preOrder.specifications) {
                  state.preOrder.specifications.splice(0, state.preOrder.specifications.length);
                }
              },
              false,
              actionName('clearSpecifications')
            );
          },

          rechargeReceived: user => {
            set(
              state => {
                if (state.preOrder.userFullInfo) {
                  // 充值，更新用户信息(余额)
                  state.preOrder.userFullInfo.user = user;
                }
              },
              false,
              actionName('rechargeReceived')
            );
          },
        }),
        {
          name: STORAGE_POS_PREORDER,
        }
      )
    ),
    {
      name: 'PreOrderStore',
    }
  )
);
